import { useEffect, useState } from "react";
import { api as apiT } from "@/lib/api";

export function TranslateBtn({ text, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onDone((await apiT.post("/translate", { text, lang: "en" })).data.text);
    } catch {
      setBusy(false);
      return;
    }
    setBusy(false);
  };
  return <button onClick={go} disabled={busy || !text} className="ml-2 rounded-full border border-[#D4E6EC] px-2 py-0.5 text-[11px] hover:border-[#0B6E8F]" data-testid="share-translate-en">{busy ? "Traduco…" : "🇬🇧 In inglese"}</button>;
}
import { toast } from "sonner";
import { Copy, MessageCircle, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { API, api, errMsg } from "@/lib/api";

const BRAND = "🌊 Afrodite Boutique Hotel • Tenuta Ledo • Catering";

const PHRASES = { Matrimonio: "il suo matrimonio", Cerimonia: "la sua cerimonia", Compleanno: "la sua festa di compleanno", Aziendale: "il vostro evento aziendale" };
export const eventPhrase = (t) => PHRASES[t] || "il suo evento";

export function signature(user) {
  return ["━━━━━━━━━━━━", user?.name && `✍️ *${user.name}*`, user?.title, user?.phone && `📞 ${user.phone}`, BRAND].filter(Boolean).join("\n");
}

export function normalizePhone(p) {
  let d = (p || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  if (d.startsWith("00")) d = d.slice(2);
  if (/^3\d{8,9}$/.test(d)) d = "39" + d;
  return d;
}

async function shareFile(url, name, text) {
  const r = await fetch(url);
  const blob = await r.blob();
  const file = new File([blob], name, { type: "application/pdf" });
  if (!navigator.canShare || !navigator.canShare({ files: [file] })) return false;
  await navigator.share({ files: [file], text });
  return true;
}

export function ShareDialog({ open, onOpenChange, doc, phone, body, fileName, link, linkLabel }) {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [tel, setTel] = useState("");
  const [url, setUrl] = useState(link || "");

  useEffect(() => {
    if (!open) return;
    setTel(phone || "");
    setMsg(`${body}\n\n${signature(user)}`);
    if (link) {
      setUrl(link);
      return;
    }
    setUrl("");
    api.post("/share", doc).then((r) => setUrl(`${API.replace(/\/api$/, "")}${r.data.path}`)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fullText = url ? `${msg}\n\n${link ? linkLabel || "🔗 Link per firmare:" : "📎 Documento PDF:"} ${url}` : msg;
  const wa = () => {
    const n = normalizePhone(tel);
    window.open(`https://api.whatsapp.com/send?phone=${n}&text=${encodeURIComponent(fullText)}`, "_blank");
  };
  const native = async () => {
    try {
      const ok = await shareFile(url, fileName, msg);
      if (!ok) toast.info("Su questo dispositivo usa «Invia con WhatsApp»: il PDF arriva come link");
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Condivisione non riuscita");
    }
  };
  const canNative = !link && typeof navigator !== "undefined" && !!navigator.canShare && /Android|iPhone|iPad/i.test(navigator.userAgent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-serif-it text-2xl">Invia al cliente su WhatsApp</DialogTitle></DialogHeader>
        <label className="block text-xs font-medium text-[#47606B]">Numero WhatsApp del cliente
          <input data-testid="share-phone" value={tel} onChange={(e) => setTel(e.target.value)} placeholder="es. 333 1234567"
            className="mt-1 w-full rounded-md border border-[#D4E6EC] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30" />
        </label>
        <label className="block text-xs font-medium text-[#47606B]">Messaggio (modificabile) <TranslateBtn text={msg} onDone={setMsg} />
          <textarea data-testid="share-message" rows={13} value={msg} onChange={(e) => setMsg(e.target.value)}
            className="mt-1 w-full rounded-md border border-[#D4E6EC] px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30" />
        </label>
        <div className="text-xs text-[#7B929C] break-all" data-testid="share-link">{url ? `${link ? "Link" : "PDF"}: ${url}` : "Preparazione del documento…"}</div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full bg-[#25D366] hover:bg-[#1eb457] text-white" disabled={!url || !normalizePhone(tel)} onClick={wa} data-testid="share-whatsapp-button"><MessageCircle size={15} className="mr-2" /> Invia con WhatsApp</Button>
          {canNative && <Button variant="outline" className="rounded-full" disabled={!url} onClick={native} data-testid="share-native-button"><Share2 size={15} className="mr-2" /> Condividi il PDF</Button>}
          <Button variant="ghost" className="rounded-full" disabled={!url} onClick={() => { navigator.clipboard?.writeText(fullText); toast.success("Messaggio copiato"); }} data-testid="share-copy-button"><Copy size={15} className="mr-2" /> Copia</Button>
        </div>
        {!user?.title && <p className="text-[11px] text-[#7B929C]">Suggerimento: imposta nome e cognome, ruolo e telefono della tua firma cliccando sul tuo nome in alto.</p>}
      </DialogContent>
    </Dialog>
  );
}

export function ShareButton({ testId, label = "Invia su WhatsApp", size, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size={size} className="rounded-full border-[#25D366] text-[#128C4A] hover:bg-[#25D366]/10" onClick={() => setOpen(true)} data-testid={testId}>
        <MessageCircle size={15} className="mr-2" /> {label}
      </Button>
      <ShareDialog open={open} onOpenChange={setOpen} {...props} />
    </>
  );
}
